import { useState } from 'react';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { SkuBaseForecastRow, EditedRow, updateWeeklyDistributionRates, WeeklyDistributionRate } from './forecastTableUtils';

export interface ResetDialogState {
    open: boolean;
    rowId: number | string;
    week: string;
    currentValue: number;
    baseValue: number;
}

export type FieldType = 'distribution' | 'rateOfSale' | 'actualDistributionRate';

interface UseResetDialogOptions<T = EditedRow> {
    rows: SkuBaseForecastRow[];
    editedRows: Record<string, T>;
    setEditedRows: React.Dispatch<React.SetStateAction<Record<string, T>>>;
    apiRef: React.MutableRefObject<GridApiPremium>;
    setSnackbar: (snackbar: any) => void;
    fieldType: FieldType;
    setUnsavedCellChanges?: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Shared hook for managing reset dialog state and logic
 * Handles both per-week overrides (base grid) and effective-from overrides (distribution/ROS)
 */
export const useResetDialog = <T extends EditedRow | SkuBaseForecastRow>({
    rows,
    editedRows,
    setEditedRows,
    apiRef,
    setSnackbar,
    fieldType,
    setUnsavedCellChanges
}: UseResetDialogOptions<T>) => {
    const [resetDialog, setResetDialog] = useState<ResetDialogState>({
        open: false,
        rowId: 0,
        week: '',
        currentValue: 0,
        baseValue: 0
    });

    const handleResetDialogClose = () => {
        setResetDialog({ ...resetDialog, open: false });
    };

    const handleResetConfirm = () => {
        const { rowId, week, baseValue } = resetDialog;
        
        const row = (editedRows[rowId] as EditedRow)?.updated || 
                    editedRows[rowId] as SkuBaseForecastRow || 
                    rows.find(r => r.id === rowId);
        
        if (!row) return;

        let updatedRow: SkuBaseForecastRow;
        let updatedWeeklyRates: WeeklyDistributionRate[];

        const overrideFlagMap: Record<FieldType, string> = {
            'actualDistributionRate': 'hasDistributionRateOverride',
            'distribution': 'hasDistributionOverride',
            'rateOfSale': 'hasRateOfSaleOverride'
        };
        
        const specificOverrideFlag = overrideFlagMap[fieldType];

        if (fieldType === 'actualDistributionRate') {
            updatedWeeklyRates = row.weeklyDistributionRates.map(w => {
                if (w.week === week) {
                    return { ...w, actualDistributionRate: baseValue, [specificOverrideFlag]: false };
                }
                return w;
            });
            
            updatedRow = { ...row, weeklyDistributionRates: updatedWeeklyRates, [week]: baseValue };
        } else {
            // For effective-from grids (distribution, rateOfSale, unitCost), send null to reset
            // Display baseValue in UI but send null to backend (handled in save logic)
            // The backend will handle the cascade effect and delete or create null override as needed
            updatedWeeklyRates = row.weeklyDistributionRates.map(w =>
                w.week === week ? { ...w, [specificOverrideFlag]: false, [fieldType]: baseValue } : w
            );
            updatedRow = { ...row, weeklyDistributionRates: updatedWeeklyRates };
        }

        const originalRow = (editedRows[rowId] as EditedRow)?.original || row;

        if (fieldType === 'actualDistributionRate') {
            setEditedRows(prev => ({
                ...prev,
                [rowId]: updatedRow as T
            }));
        } else {
            setEditedRows(prev => ({
                ...prev,
                [rowId]: {
                    original: originalRow,
                    updated: updatedRow,
                } as T
            }));
        }

        if (setUnsavedCellChanges) {
            setUnsavedCellChanges(prev => new Set(prev).add(`${rowId}-${week}`));
        }

        // Update grid display - only update the specific week being reset
        // The cascade effect will be visible after save and refresh
        apiRef.current.updateRows([updatedRow]);

        setResetDialog({ ...resetDialog, open: false });
    };

    const openResetDialog = (rowId: number | string, week: string, currentValue: number, baseValue: number) => {
        setResetDialog({
            open: true,
            rowId,
            week,
            currentValue,
            baseValue
        });
    };

    return {
        resetDialog,
        handleResetDialogClose,
        handleResetConfirm,
        openResetDialog
    };
};

